import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { MatterCreatorBasic } from './basic';
import { MatterCreatorFiles } from './files';
import { MatterCreatorIssuesAndContext } from './issues-context';
import { MatterCreatorProcess } from './process';
import { BasicInfoForm, makeBasicInfoSchema } from './schema';
import { CONTEXT_FILES_MAX_TOTAL_SIZE } from '../../../../../../../backend/src/constants/context-file-limits';
import { FallbackDateFormat, MatterLanguage } from '../../../../../../../backend/src/types/types';
import { Icon } from '../../../../components/icon';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import { useSubmitMatter } from '@/api/mutations/useSubmitMatter';
import { useSyncMatterParties } from '@/api/mutations/useSyncMatterParties';
import { useSyncSubsetIssues } from '@/api/mutations/useSyncSubsetIssues';
import { useUpdateMatter } from '@/api/mutations/useUpdateMatter';
import { useGetFiles } from '@/api/queries/useGetFiles';
import { useGetMatterParties } from '@/api/queries/useGetMatterParties';
import { useGetMatterUploadHistory } from '@/api/queries/useGetMatterUploadHistory';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSubsetIssues } from '@/api/queries/useGetSubsetIssues';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';
import { useUserContext } from '@/Contexts/User';
import { Breadcrumb } from '@/new/components/breadcrumb';
import type { IssueFormItem } from '@/new/components/matter/issues-list';
import type { PartyFormItem } from '@/new/components/matter/parties-list';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';
import { Form } from '@/new/components/shadcn/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';
import { useProjectedCreditLimitBlock } from '@/new/hooks/use-projected-credit-limit';

const TABS = [
  {
    value: 'upload-documents',
    label: 'Upload',
    description: 'Wexler accepts a wide range of file formats. Try uploading witness statements, emails, or contracts.',
  },
  {
    value: 'basic-information',
    label: 'Basic Information',
    description: 'Give this new Matter a name and optionally set page limits.',
  },
  {
    value: 'issues-context',
    label: 'Issues & Context',
    description: 'Define matter issues, parties, and additional context to help Wexler focus its analysis.',
  },
  {
    value: 'process',
    label: 'Review',
    description: 'Review the details below and begin processing your Matter.',
  },
] as const;

type TabValue = (typeof TABS)[number]['value'];

const DEFAULT_TAB: TabValue = 'upload-documents';

function getHashTab(): TabValue {
  const hash = window.location.hash.slice(1);
  if (TABS.some((t) => t.value === hash)) return hash as TabValue;
  return DEFAULT_TAB;
}

export function MatterCreator() {
  const { matterId } = useParams();
  const navigate = useNavigate();
  const { getFetchConfig } = useGetFetchConfig();
  const { user } = useUserContext();
  const requireMatterCode = user?.organisation?.requireMatterCode ?? false;

  const [activeTab, setActiveTab] = useState<TabValue>(getHashTab);
  const [hasContextFiles, setHasContextFiles] = useState(false);
  const [showIssuesError, setShowIssuesError] = useState(false);

  const { data: matter } = useGetMatterV2(matterId);
  const { data: existingParties } = useGetMatterParties(matterId);
  const { data: files } = useGetFiles(matterId ?? '');
  const { wouldExceed: wouldExceedCreditLimit, message: projectedCreditLimitMessage } = useProjectedCreditLimitBlock(
    matterId,
    files,
  );
  // Single source of truth for upload_history — FileUploader receives this via prop.
  const { data: uploadHistory, isLoading: isUploadHistoryLoading } = useGetMatterUploadHistory(matterId ?? '', {
    refetchInterval: 5_000,
  });
  const hasActiveProcessingUploads = uploadHistory?.some((u) => u.status === 'processing') ?? false;

  const allDocumentsCaseId = matter?.allDocumentsCaseId ?? undefined;
  const { data: serverIssues } = useGetSubsetIssues(matterId, allDocumentsCaseId);

  // Local state for issues and parties
  const [localIssues, setLocalIssues] = useState<IssueFormItem[]>([]);
  const [localParties, setLocalParties] = useState<PartyFormItem[]>([]);
  const [issuesSeeded, setIssuesSeeded] = useState(false);
  const [partiesSeeded, setPartiesSeeded] = useState(false);
  const [filesProcessing, setFilesProcessing] = useState(false);

  // Seed local state from server data
  useEffect(() => {
    if (serverIssues && !issuesSeeded) {
      setLocalIssues(serverIssues.map((i) => ({ id: i.id, label: i.label, text: i.text })));
      setIssuesSeeded(true);
    }
  }, [serverIssues, issuesSeeded]);

  useEffect(() => {
    if (existingParties && !partiesSeeded) {
      setLocalParties(
        existingParties.map((p) => ({
          id: p.id,
          name: p.name,
          isClient: p.isClient,
          role: p.role ?? 'other',
          type: p.type === 'place' ? null : p.type,
        })),
      );
      setPartiesSeeded(true);
    }
  }, [existingParties, partiesSeeded]);

  // Default to showing the issues/context form if data already exists
  useEffect(() => {
    if ((serverIssues && serverIssues.length > 0) || (existingParties && existingParties.length > 0)) {
      setHasContextFiles(true);
    }
  }, [serverIssues, existingParties]);

  const [additionalContext, setAdditionalContext] = useState('');
  const [contextSeeded, setContextSeeded] = useState(false);
  const queryClient = useQueryClient();
  const updateMatter = useUpdateMatter(matterId);
  const submitMatter = useSubmitMatter(matterId);
  const syncIssues = useSyncSubsetIssues(matterId ?? '', allDocumentsCaseId);
  const syncParties = useSyncMatterParties(matterId ?? '');

  const [justExtracted, setJustExtracted] = useState(false);
  const [showAiReviewModal, setShowAiReviewModal] = useState(false);
  const [pendingTab, setPendingTab] = useState<TabValue | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionCounts, setExtractionCounts] = useState({ issues: 0, parties: 0, suggestions: 0 });
  const abortRef = useRef<AbortController | null>(null);

  const handleContextFilesUploaded = useCallback(
    async (uploadedFiles: File[]) => {
      if (!matterId) return;

      // Check the total size of the uploaded files
      const totalSize = uploadedFiles.reduce((sum, file) => sum + file.size, 0);
      if (totalSize > CONTEXT_FILES_MAX_TOTAL_SIZE) {
        toast.error('Total file size exceeds 30MB limit');
        return;
      }

      abortRef.current?.abort();
      const abort = new AbortController();
      abortRef.current = abort;

      trackCustomEvent(TrackingEventsV2.MatterContextUpload, {
        matterId,
        fileCount: uploadedFiles.length,
      });

      setIsExtracting(true);
      setExtractionCounts({ issues: 0, parties: 0, suggestions: 0 });
      let latestCounts = { issues: 0, parties: 0, suggestions: 0 };

      const formData = new FormData();
      uploadedFiles.forEach((file) => formData.append('files', file));

      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: formData as unknown as Record<string, unknown>,
        isFormData: true,
      });

      try {
        const response = await fetch(`${APIBaseChronos}/api/matters/${matterId}/generate-context`, {
          ...fetchConfig,
          signal: abort.signal,
        });

        if (!response.ok || !response.body) throw new Error('Failed to generate context');

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let serverError: string | null = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === 'progress') {
                latestCounts = {
                  issues: event.issueCount,
                  parties: event.partyCount,
                  suggestions: event.suggestionCount,
                };
                setExtractionCounts(latestCounts);
              } else if (event.type === 'done') {
                // Await invalidations so fresh data is in the cache before re-seeding
                await Promise.all([
                  queryClient.invalidateQueries([QueryEntity.Matter, { matterId }]),
                  queryClient.invalidateQueries([QueryEntity.SubsetIssues]),
                  queryClient.invalidateQueries([QueryEntity.MatterParties, { matterId }]),
                ]);
                setIssuesSeeded(false);
                setPartiesSeeded(false);
                setContextSeeded(false);
                setHasContextFiles(true);
                setJustExtracted(true);
                if (latestCounts.issues === 0 && latestCounts.parties === 0 && latestCounts.suggestions === 0) {
                  toast.info(
                    "We couldn't extract any context from these files. Try uploading different files or fill out details manually.",
                  );
                }
              } else if (event.type === 'error') {
                serverError = typeof event.message === 'string' ? event.message : 'Failed to extract context.';
              }
            } catch {
              // skip malformed lines
            }
          }
        }

        if (serverError) {
          toast.error(serverError);
        }
      } catch {
        toast.error('Failed to extract context. Please try again.');
      } finally {
        setIsExtracting(false);
      }
    },
    [matterId, getFetchConfig, queryClient],
  );

  useEffect(() => {
    const onHashChange = () => setActiveTab(getHashTab());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const saveCurrentTab = async (): Promise<boolean> => {
    if (activeTab === 'basic-information' && matterId) {
      let wasValid = false;
      const onValid = async (values: BasicInfoForm) => {
        wasValid = true;
        const code = values.code?.trim();
        await updateMatter.mutateAsync({
          name: values.name.trim(),
          code: code ? code : undefined,
          tokenLimit: values.tokenLimit,
          language: values.language,
          fallbackDateFormat: values.fallbackDateFormat,
        });
      };

      const onInvalid = () => {
        wasValid = false;
      };

      await basicForm.handleSubmit(onValid, onInvalid)();
      return wasValid;
    }

    if (activeTab === 'issues-context' && matterId && allDocumentsCaseId) {
      await Promise.all([
        syncIssues.mutateAsync(localIssues),
        syncParties.mutateAsync(localParties),
        updateMatter.mutateAsync({ additionalContext }),
      ]);
    }

    return true;
  };

  const handleTabChange = async (value: string) => {
    // Show AI review modal when first navigating away from issues-context after extraction
    if (activeTab === 'issues-context' && justExtracted) {
      setPendingTab(value as TabValue);
      setShowAiReviewModal(true);
      return;
    }

    const saved = await saveCurrentTab();
    if (!saved) return;
    setActiveTab(value as TabValue);
    window.history.replaceState(null, '', `#${value}`);
  };

  const confirmAiReview = async () => {
    setShowAiReviewModal(false);
    setJustExtracted(false);
    if (pendingTab) {
      const saved = await saveCurrentTab();
      if (!saved) return;
      setActiveTab(pendingTab);
      window.history.replaceState(null, '', `#${pendingTab}`);
      setPendingTab(null);
    }
  };

  const basicInfoSchema = useMemo(() => makeBasicInfoSchema(requireMatterCode), [requireMatterCode]);
  const basicForm = useForm<BasicInfoForm>({
    resolver: zodResolver(basicInfoSchema),
    defaultValues: {
      name: '',
      code: '',
      tokenLimit: undefined,
      // Placeholder only; replaced by the matter's persisted (region-correct) language on load.
      language: MatterLanguage.EnglishUK,
      fallbackDateFormat: FallbackDateFormat.Auto,
    },
  });

  useEffect(() => {
    if (matter) {
      basicForm.reset({
        name: matter.name ?? '',
        code: matter.code ?? '',
        tokenLimit: matter.tokenLimit ?? undefined,
        language: matter.language,
        fallbackDateFormat: matter.fallbackDateFormat,
      });

      if (matter.additionalContext != null && !contextSeeded) {
        setAdditionalContext(matter.additionalContext);
        setContextSeeded(true);
      }
    }
  }, [matter, contextSeeded]);

  const canSubmit =
    !!matter?.name?.trim() &&
    localIssues.length > 0 &&
    (files?.length ?? 0) > 0 &&
    !isUploadHistoryLoading &&
    !hasActiveProcessingUploads &&
    !filesProcessing;

  const activeIndex = TABS.findIndex((t) => t.value === activeTab);

  // Returning to file upload exits the post-extraction review state, so clear
  // justExtracted to avoid re-triggering the AI review modal on the next Next.
  const returnToUpload = () => {
    setHasContextFiles(false);
    setJustExtracted(false);
  };

  const goBack = async () => {
    // Within Issues & Context, the review sub-stage steps back to file upload
    // rather than jumping to the previous top-level section.
    if (activeTab === 'issues-context' && hasContextFiles) {
      returnToUpload();
      return;
    }
    if (activeIndex > 0) handleTabChange(TABS[activeIndex - 1].value);
  };

  const skipContextUpload = () => {
    trackCustomEvent(TrackingEventsV2.MatterContextSkipUpload, { matterId });
    setHasContextFiles(true);
  };

  const goNext = async () => {
    if (activeTab === 'issues-context' && !hasContextFiles) {
      skipContextUpload();
      return;
    }
    if (activeTab === 'issues-context' && localIssues.length === 0) {
      setShowIssuesError(true);
      return;
    }
    if (activeIndex < TABS.length - 1) {
      handleTabChange(TABS[activeIndex + 1].value);
    } else {
      if (wouldExceedCreditLimit) return;
      // Final tab - save then create the matter
      const saved = await saveCurrentTab();
      if (!saved) return;
      await handleCreateMatter();
    }
  };

  const handleCreateMatter = async () => {
    if (!matterId) return;
    await submitMatter.mutateAsync();
    navigate(`/matters/${matterId}`);
  };

  if (!matterId) return null;

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb
        items={[
          { href: '/matters', label: 'Matters', active: false },
          { href: `/matters/new/${matterId}`, label: 'New Matter', active: true },
        ]}
      />

      <Tabs value={activeTab} onValueChange={handleTabChange} className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="shrink-0 border-b p-4 px-5">
          <div className="flex items-center justify-between">
            <div>
              <TabsList className="relative h-auto w-full justify-start border-0">
                {TABS.map((tab, i) => (
                  <span key={tab.value} className="flex items-center">
                    {i > 0 && <Icon className="lucide--arrow-right mx-1 mt-px text-gray-300" />}

                    <TabsTrigger
                      value={tab.value}
                      className="border-b-0 py-1.5 font-serif text-[22px] text-gray-300 duration-150 hover:text-gray-400 data-[state=active]:mb-0 data-[state=active]:border-b-0 data-[state=active]:text-gray-900"
                    >
                      {tab.label}
                    </TabsTrigger>
                  </span>
                ))}
              </TabsList>
              <p className="mb-3 px-3 text-xs text-gray-500">{TABS.find((t) => t.value === activeTab)?.description}</p>
            </div>

            <div className="flex shrink-0 items-center gap-4 pr-4">
              <div className="flex items-center gap-1">
                <Button variant="ghost" onClick={goBack} disabled={activeIndex === 0}>
                  <Icon className="lucide--arrow-left" />
                  Back
                </Button>
                <Button
                  variant="ghost"
                  onClick={goNext}
                  disabled={
                    updateMatter.isLoading ||
                    submitMatter.isLoading ||
                    (activeIndex === TABS.length - 1 && (!canSubmit || wouldExceedCreditLimit))
                  }
                >
                  {activeIndex === TABS.length - 1 ? 'Create Matter' : 'Next'}
                  <Icon className="lucide--arrow-right" />
                </Button>
              </div>

              <div className="flex gap-2.5">
                {TABS.map((_, i) => (
                  <div
                    key={i}
                    className={`h-1.5 rounded-full transition-all duration-150 ease-in-out ${
                      i === activeIndex
                        ? 'w-6 bg-teal-400'
                        : i < activeIndex
                          ? 'w-1.5 bg-teal-500/60'
                          : 'w-1.5 bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <TabsContent value="upload-documents" forceMount className="mt-0 min-h-0 flex-1 data-[state=inactive]:hidden">
          <MatterCreatorFiles uploadHistory={uploadHistory} setFilesProcessing={setFilesProcessing} />
        </TabsContent>

        <div
          className="min-h-0 flex-1 overflow-y-auto data-[hidden]:hidden"
          data-hidden={activeTab === 'upload-documents' ? '' : undefined}
        >
          <TabsContent value="basic-information" forceMount className="mt-6 data-[state=inactive]:hidden">
            <Form {...basicForm}>
              <MatterCreatorBasic form={basicForm} requireCode={requireMatterCode} />
            </Form>
          </TabsContent>

          <TabsContent
            value="issues-context"
            forceMount
            className={`${hasContextFiles ? 'mt-6' : 'mt-0 h-full'} data-[state=inactive]:hidden`}
          >
            <MatterCreatorIssuesAndContext
              hasContextFiles={hasContextFiles}
              onSkipUpload={skipContextUpload}
              onShowUpload={returnToUpload}
              onFilesSelected={handleContextFilesUploaded}
              isUploading={isExtracting}
              extractionCounts={extractionCounts}
              showSuggestedQuestions={justExtracted}
              additionalContext={additionalContext}
              setAdditionalContext={setAdditionalContext}
              issues={localIssues}
              onIssuesChange={(next) => {
                setLocalIssues(next);
                if (next.length > 0) setShowIssuesError(false);
              }}
              showIssuesError={showIssuesError && localIssues.length === 0}
              parties={localParties}
              onPartiesChange={setLocalParties}
            />
          </TabsContent>

          <TabsContent value="process" forceMount className="mt-6 data-[state=inactive]:hidden">
            <MatterCreatorProcess
              issues={localIssues}
              parties={localParties}
              additionalContext={additionalContext}
              creditLimitMessage={projectedCreditLimitMessage}
            />
          </TabsContent>
        </div>
      </Tabs>

      {/* AI Review Modal */}
      <Dialog open={showAiReviewModal} onOpenChange={setShowAiReviewModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Icon className="lucide--scan-search" />
              Review Issues
            </DialogTitle>
          </DialogHeader>
          <DialogDescription>Verify any AI extracted content before proceeding.</DialogDescription>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAiReviewModal(false)}>
              Go Back & Review
            </Button>
            <Button onClick={confirmAiReview}>Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
